// Shared dropdown surface for InsertBlockMenu, TypePickerMenu and the context
// menu; LineAutocomplete and CueExtensionPopover borrow its class strings. Owns
// its Tailwind classes so consumers don't need a CSS module to share visuals.
//
// Concept reference: `.type-menu` / `.type-menu-item` in
// `editor-concept/editor.html` (radii 11px / 7px, soft shadow, paper bg).
// `ChromeMenuItem.selected` mirrors the hover state for keyboard navigation
// (QA §2.3.3).

import Popover, { type PopoverProps } from '@mui/material/Popover';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface ChromeMenuProps {
    anchorEl: PopoverProps['anchorEl'];
    open?: boolean;
    onClose: () => void;
    anchorOrigin?: PopoverProps['anchorOrigin'];
    transformOrigin?: PopoverProps['transformOrigin'];
    children: ReactNode;
}

/** Shared Tailwind class string for the menu container surface. Exported so
 *  `LineAutocomplete` (which renders a `<Popper>` rather than `<Popover>` and
 *  therefore doesn't have access to the Paper slot) can apply the same look
 *  to its own outer `<div>`. */
export const chromeMenuPaperClass =
    'min-w-[162px] p-[5px] rounded-[11px] border border-c5 bg-bg-paper text-text-primary shadow-[0_12px_32px_rgba(0,0,0,0.10)] font-sans text-[13px] [background-image:none]';

export function ChromeMenu({
    anchorEl,
    open = true,
    onClose,
    anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
    transformOrigin = { vertical: 'top', horizontal: 'left' },
    children,
}: ChromeMenuProps) {
    return (
        <Popover
            open={open}
            anchorEl={anchorEl ?? null}
            onClose={onClose}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            disableAutoFocus
            disableEnforceFocus
            slotProps={{
                paper: {
                    className: chromeMenuPaperClass,
                    elevation: 0,
                    // Repeats `chromeMenuPaperClass`'s background over MUI Paper's
                    // own (and its elevation overlay). Since ADR 0017 put MUI's
                    // CSS in a layer below Tailwind's utilities, the class
                    // already wins on its own.
                    sx: { backgroundColor: 'var(--color-bg-paper)', backgroundImage: 'none' },
                },
            }}
        >
            {children}
        </Popover>
    );
}

interface ChromeMenuItemProps {
    onClick: () => void;
    /** Renders ✓ on the left of the label (used to mark current selection). */
    current?: boolean;
    /** Same surface as `:hover` — applied for keyboard cursor in lists. */
    selected?: boolean;
    /** Greys the row out: the operation belongs here but cannot run right now
     *  (nothing to set aside, already a draft, the type the block already is).
     *  Shown rather than hidden — concept § 4.6 «недоступные строки серые, не
     *  спрятаны». The click is still reported so the menu can close on it; the
     *  caller decides what an inert row does. */
    disabled?: boolean;
    /** Optional 14×14 type icon rendered between the check column and the label
     *  (concept prototype `openTypeMenu` row layout `[check][icon][label]`). */
    icon?: ReactNode;
    /** Optional decoration shown to the right of the label (e.g. shortcut hint). */
    trailing?: ReactNode;
    /** Destructive row — red, and by convention the last one in its menu
     *  (concept § 4.6: «Удалить» всегда последнее и красное). */
    danger?: boolean;
    /** Native tooltip — the context menu says through it WHY a row is grey
     *  («Буфер блоков пуст»). */
    title?: string;
    /** Hover, for menus whose keyboard cursor is a class rather than focus:
     *  moving the mouse has to move that cursor too. */
    onMouseEnter?: () => void;
    children: ReactNode;
}

const ITEM_BASE = [
    'flex w-full items-center gap-2',
    'px-[9px] py-[6px]',
    'rounded-[7px] border-none bg-transparent',
    'text-left text-[13px] leading-[1.2] text-text-primary',
    'cursor-pointer',
].join(' ');

export function ChromeMenuItem({
    onClick,
    current,
    selected,
    disabled,
    icon,
    trailing,
    danger,
    title,
    onMouseEnter,
    children,
}: ChromeMenuItemProps) {
    return (
        <button
            type="button"
            aria-disabled={disabled || undefined}
            title={title}
            className={cn(
                ITEM_BASE,
                // twMerge, not string concat: `cursor-default` has to actually
                // replace ITEM_BASE's `cursor-pointer`, not race it in the sheet.
                disabled ? 'cursor-default opacity-40' : 'hover:bg-c7',
                selected && 'bg-c7',
                current && 'font-semibold',
                danger && 'text-error [&_svg]:text-error',
                danger && !disabled && 'hover:bg-error/10',
                danger && selected && 'bg-error/10',
            )}
            onMouseEnter={onMouseEnter}
            onMouseDown={e => e.preventDefault()}
            onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onClick();
            }}
        >
            {/* Reserve the check column on icon menus (visibility-toggled) so the
                icon / label don't shift between current and non-current rows —
                mirrors the prototype's `order:-1` + `visibility:hidden` check
                column. Text-only menus without a current row render no check at
                all, keeping their flush-left layout. */}
            {(icon || current) && <span className={`text-chrome-icon shrink-0 ${current ? '' : 'invisible'}`}>✓</span>}
            {icon && (
                <span className="inline-flex h-[14px] w-[14px] shrink-0 items-center justify-center [&_svg]:block [&_svg]:h-[14px] [&_svg]:w-[14px]">
                    {icon}
                </span>
            )}
            <span className="flex-1">{children}</span>
            {trailing && <span className="ml-auto text-chrome-icon">{trailing}</span>}
        </button>
    );
}

/** Lower-level wrapper for non-button items (e.g. `<li>` in LineAutocomplete
 *  that wants the same surface but its own onClick semantics). Keeps the
 *  Tailwind class string in one place. */
export const chromeMenuItemClass = (selected?: boolean) => `${ITEM_BASE} hover:bg-c7 ${selected ? 'bg-c7' : ''}`;
